import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "../i18n";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  reasonRequired: boolean;
  reasonMinLength?: number;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  reasonRequired,
  reasonMinLength = 10,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element {
  const [reason, setReason] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);

  const canConfirm = !reasonRequired || reason.trim().length >= reasonMinLength;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  const handleConfirm = useCallback((): void => {
    if (!canConfirm) return;
    onConfirm(reason.trim());
    setReason("");
  }, [canConfirm, reason, onConfirm]);

  const handleCancel = useCallback((): void => {
    setReason("");
    onCancel();
  }, [onCancel]);

  const handleDialogCancel = useCallback(
    (e: React.SyntheticEvent): void => {
      e.preventDefault();
      handleCancel();
    },
    [handleCancel],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>): void => {
      if (e.target === dialogRef.current) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDialogElement>): void => {
      if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleCancel],
  );

  const charsRemaining = reasonMinLength - reason.trim().length;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="confirm-title"
      style={styles.dialog}
      onCancel={handleDialogCancel}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div style={styles.content}>
        <h2 id="confirm-title" style={styles.title}>
          {title}
        </h2>
        <p style={styles.description}>{description}</p>

        {reasonRequired && (
          <ReasonField
            reason={reason}
            minLength={reasonMinLength}
            charsRemaining={charsRemaining}
            onChange={setReason}
          />
        )}

        <div style={styles.actions}>
          <button type="button" onClick={handleCancel} style={styles.cancelBtn}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              ...styles.confirmBtn,
              ...(canConfirm ? {} : styles.confirmBtnDisabled),
            }}
          >
            {t("common.confirm")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function ReasonField({
  reason,
  minLength,
  charsRemaining,
  onChange,
}: {
  reason: string;
  minLength: number;
  charsRemaining: number;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={styles.field}>
      <label htmlFor="confirm-reason" style={styles.label}>
        {t("confirm.reason_label", { min: String(minLength) })}
      </label>
      <textarea
        id="confirm-reason"
        value={reason}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("confirm.reason_placeholder")}
        rows={3}
        style={styles.textarea}
      />
      {reason.length > 0 && charsRemaining > 0 && (
        <p style={styles.hint}>
          {t(
            charsRemaining === 1
              ? "confirm.chars_remaining_one"
              : "confirm.chars_remaining_other",
            { count: charsRemaining },
          )}
        </p>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  dialog: {
    border: "none",
    borderRadius: 8,
    padding: 0,
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
  },
  content: {
    padding: 24,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    lineHeight: 1.6,
    marginBottom: 16,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 6,
    color: "var(--color-text-secondary)",
  },
  textarea: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    resize: "vertical" as const,
    fontSize: 14,
    lineHeight: 1.5,
  },
  hint: {
    fontSize: 12,
    color: "var(--color-danger)",
    marginTop: 4,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  cancelBtn: {
    padding: "8px 16px",
    fontSize: 14,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  confirmBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 6,
    border: "none",
    background: "var(--color-danger)",
    color: "#ffffff",
  },
  confirmBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
};
